import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { suppressionsApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";

const TYPES = [
  "do_not_contact", "not_interested", "unsuitable", "existing_customer",
  "duplicate", "competitor", "deferred_until_date", "other",
];
const labelize = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function SuppressionDialog({ open, onOpenChange, orgId, onSaved }) {
  const [form, setForm] = useState({ suppression_type: "not_interested", reason: "", expires_at: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm({ suppression_type: "not_interested", reason: "", expires_at: "" }); setError(""); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isDeferred = form.suppression_type === "deferred_until_date";

  const submit = async (e) => {
    e.preventDefault();
    if (isDeferred && !form.expires_at) { setError("Deferred suppression needs an expiry date"); return; }
    setSaving(true); setError("");
    try {
      await suppressionsApi.apply(orgId, {
        suppression_type: form.suppression_type,
        reason: form.reason || null,
        expires_at: isDeferred ? new Date(form.expires_at).toISOString() : null,
      });
      toast.success("Suppression applied");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { setError(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="suppression-dialog">
        <DialogHeader><DialogTitle className="font-heading">Suppress organisation</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-slate-500">Suppressing hides this org from future prospecting. Existing history is never deleted.</p>
          <Field label="Type">
            <SelectInput testid="suppression-type" value={form.suppression_type} onChange={set("suppression_type")}>
              {TYPES.map((t) => <option key={t} value={t}>{labelize(t)}</option>)}
            </SelectInput>
          </Field>
          {isDeferred && (
            <Field label="Defer until"><TextInput testid="suppression-expires" type="datetime-local" value={form.expires_at} onChange={set("expires_at")} /></Field>
          )}
          <Field label="Reason"><TextInput testid="suppression-reason" value={form.reason} onChange={set("reason")} /></Field>
          {error && <p data-testid="suppression-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving} data-testid="suppression-submit"
            className="min-h-[48px] w-full rounded-lg bg-rose-600 font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
            {saving ? "Saving…" : "Apply suppression"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
